// src/pages/DetailView.tsx
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GIFT_GROUPS } from "../constants/categories";
import { StartCustomizationModal } from "../components/recipient/StartCustomizationModal";
import RadialCard from "../components/card/RadialCard";
import { ArchetypeId } from "../constants/archetypes";

export default function DetailView() {
  const { archetypeId } = useParams<{ archetypeId: ArchetypeId }>();
  const navigate = useNavigate();
  const dragX = useMotionValue(0);

  // Mouse Tracking for Interactive Background
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const smoothX = useSpring(mouseX, { damping: 50, stiffness: 100 });
  const smoothY = useSpring(mouseY, { damping: 50, stiffness: 100 });
  const bgX = useTransform(smoothX, [-0.5, 0.5], ["-10%", "10%"]);
  const bgY = useTransform(smoothY, [-0.5, 0.5], ["-5%", "5%"]);

  // All hooks must be called at the top level
  const [activeDisplayIndex, setActiveDisplayIndex] = useState(0);
  const [showStartModal, setShowStartModal] = useState(false);
  const [radius, setRadius] = useState(0);
  const scrollAccumulator = useRef(0);
  const SCROLL_THRESHOLD = 280;
  const CARD_WIDTH = 280;

  useEffect(() => {
    // Set document title
    document.title = "Who is this for? - Lrnsy";
  }, []);

  // Get all subcategories for the selected archetype
  const subs = useMemo(() => {
    if (!archetypeId) return [];

    // Filter subcategories by archetype across all groups
    return GIFT_GROUPS.flatMap((group) =>
      group.subcategories.filter((sub) => sub.archetype === archetypeId)
    );
  }, [archetypeId]);

  const displayItems = useMemo(() => {
    if (subs.length === 0) return [];
    return [...subs, ...subs];
  }, [subs]);

  // THEME COLOR ENGINE (High Visibility)
  const themeColors = {
    moments: {
      primary: "rgba(234, 179, 8, 0.5)",
      secondary: "rgba(234, 179, 8, 0.25)",
    },
    heartbeat: {
      primary: "rgba(239, 68, 68, 0.5)",
      secondary: "rgba(239, 68, 68, 0.25)",
    },
    safehaven: {
      primary: "rgba(59, 130, 246, 0.5)",
      secondary: "rgba(59, 130, 246, 0.25)",
    },
    default: {
      primary: "rgba(255, 255, 255, 0.1)",
      secondary: "rgba(255, 255, 255, 0.05)",
    },
  };

  const theme = archetypeId
    ? themeColors[archetypeId as keyof typeof themeColors] || themeColors.default
    : themeColors.default;

  const ANGLE_STEP = subs.length > 0 ? 270 / (subs.length * 1.5) : 0;
  const rotationOffset = activeDisplayIndex * -ANGLE_STEP;
  const activeSubData = subs.length > 0 ? subs[activeDisplayIndex % subs.length] : null;

  const handleMouseMove = (e: React.MouseEvent) => {
    mouseX.set(e.clientX / window.innerWidth - 0.5);
    mouseY.set(e.clientY / window.innerHeight - 0.5);
  };

  useEffect(() => {
    if (displayItems.length === 0) return;

    const handleWheel = (e: WheelEvent) => {
      scrollAccumulator.current += e.deltaY;
      const nextIndex = Math.round(scrollAccumulator.current / SCROLL_THRESHOLD);
      const clampedIndex = Math.max(0, Math.min(nextIndex, displayItems.length - 1));
      if (clampedIndex !== activeDisplayIndex) setActiveDisplayIndex(clampedIndex);
    };
    window.addEventListener("wheel", handleWheel, { passive: true });
    return () => window.removeEventListener("wheel", handleWheel);
  }, [activeDisplayIndex, displayItems.length]);

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      let newRadius = Math.min(w * 0.35, h * 0.45);
      setRadius(Math.max(newRadius, 250));
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleDragEnd = () => {
    if (displayItems.length === 0) return;

    const x = dragX.get();
    const threshold = window.innerWidth <= 768 ? 5 : 100;
    if (x <= -threshold)
      setActiveDisplayIndex((prev) => Math.min(prev + 1, displayItems.length - 1));
    else if (x >= threshold) setActiveDisplayIndex((prev) => Math.max(prev - 1, 0));
    dragX.set(0);
  };

  // If no subcategories found or invalid archetype - return early AFTER all hooks
  if (!archetypeId || subs.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">No Experiences Found</h2>
          <p className="text-text-tertiary mb-8">No subcategories available for this archetype.</p>
          <button
            onClick={() => navigate("/archetypes")}
            className="px-6 py-3 bg-primary text-white rounded-xl"
          >
            Back to Archetypes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      onMouseMove={handleMouseMove}
      className="relative h-screen w-full bg-black text-white overflow-hidden select-none"
    >
      {/* --- HIGH VISIBILITY THEMED BACKGROUND --- */}
      <div className="absolute inset-0 pointer-events-none z-0">
        {/* Base Glow */}
        <motion.div
          animate={{ backgroundColor: theme.secondary }}
          className="absolute inset-0 opacity-40 transition-colors duration-1000"
        />

        {/* Interactive Floating Bloom */}
        <motion.div
          style={{ x: bgX, y: bgY }}
          animate={{ backgroundColor: theme.primary }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[110vw] h-[110vw] rounded-full blur-[140px] md:blur-[220px] opacity-40 mix-blend-screen transition-colors duration-1000"
        />

        {/* The Radial Grid Geometry */}
        <div className="absolute inset-0 flex items-center justify-center opacity-[0.12]">
          <div
            className="relative w-[150vw] h-[150vw] flex items-center justify-center"
            style={{
              transform: "perspective(1200px) rotateX(65deg) rotateZ(-15deg) translateY(-10%)",
              transformStyle: "preserve-3d",
            }}
          >
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `repeating-conic-gradient(from 0deg, #fff 0deg, #fff 0.5deg, transparent 0.5deg, transparent 15deg)`,
                maskImage: "radial-gradient(circle, black 30%, transparent 80%)",
                WebkitMaskImage: "radial-gradient(circle, black 30%, transparent 80%)",
              }}
            />
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `repeating-radial-gradient(circle, #fff 0px, #fff 1px, transparent 1px, transparent 80px)`,
                maskImage: "radial-gradient(circle, black 30%, transparent 80%)",
                WebkitMaskImage: "radial-gradient(circle, black 30%, transparent 80%)",
              }}
            />
          </div>
        </div>

        {/* Global Vignette */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_10%,rgba(0,0,0,0.8)_100%)]" />
      </div>

      {/* Archetype indicator */}
      {archetypeId && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-40">
          <motion.div
            animate={{
              borderColor: theme.primary,
              backgroundColor: theme.secondary,
            }}
            className="backdrop-blur-xl border px-4 py-1.5 rounded-full shadow-[0_0_20px_rgba(0,0,0,0.5)]"
          >
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white">
              WHO IS THIS FOR?
            </span>
          </motion.div>
        </div>
      )}

      {/* Header Text (Dynamic Color) */}
      <header className="relative z-50 pt-32 text-center flex flex-col items-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSubData?.id}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
          >
            <h3
              className="text-xl font-black italic uppercase tracking-tighter transition-colors duration-700"
              style={{
                color:
                  archetypeId === "moments"
                    ? "#fbbf24"
                    : archetypeId === "heartbeat"
                      ? "#ef4444"
                      : archetypeId === "safehaven"
                        ? "#3b82f6"
                        : "#fff",
              }}
            >
              {activeSubData?.name}
            </h3>
          </motion.div>
        </AnimatePresence>
      </header>

      {/* GALLERY SECTION */}
      <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center pointer-events-none h-0">
        <motion.div
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          style={{ x: dragX }}
          onDragEnd={handleDragEnd}
          className="relative w-full flex items-center justify-center cursor-grab active:cursor-grabbing"
        >
          {displayItems.map((sub, i) => (
            <RadialCard
              key={`${sub.id}-${i}`}
              data={sub}
              index={i}
              activeIndex={activeDisplayIndex}
              radius={radius}
              angleStep={ANGLE_STEP}
              onClick={() =>
                activeDisplayIndex === i
                  ? setShowStartModal(true)
                  : (setActiveDisplayIndex(i), (scrollAccumulator.current = i * SCROLL_THRESHOLD))
              }
              cardWidth={CARD_WIDTH}
              rotationOffset={rotationOffset}
            />
          ))}
        </motion.div>
      </div>

      {/* MODAL */}
      <AnimatePresence>
        {showStartModal && activeSubData && (
          <StartCustomizationModal
            recipient={activeSubData}
            onClose={() => setShowStartModal(false)}
            onStart={() => {
              const params = new URLSearchParams();
              params.set("subcategory", activeSubData.id);
              if (activeSubData.archetype) params.set("archetype", activeSubData.archetype);

              // Navigate directly to create with params
              navigate(`/create?${params.toString()}`);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
